import { FC, useState } from 'react';
import { Form, RadioGroup } from 'rsuite';
import InfoOutlineIcon from '@rsuite/icons/InfoOutline';

import { RsButton } from 'components/Rsuite/Button';
import { RadioCell } from 'components/Rsuite/Radio';
import { RsSelect, RsSelectSize } from 'components/Rsuite/Select';
import { RsInputSize } from 'components/Rsuite/Input';
import { Hint } from 'components/Rsuite/Hint';

import {
  ManualKeywordProperty,
  MANUAL_UNIQUE_KEY,
  ManualKeywordType,
} from 'core/rtbCampaign/singlePage/keyword';

import { BlurNumberInput } from '../BlurNumberInput';
import styles from './bidBatchEdit.module.scss';

enum PriceMode {
  OFFSET_PRICE = 'offsetPrice',
  ABSOLUTE_PRICE = 'absolutePrice',
  RECOMMENDED_PRICE = 'recommendedPrice',
}

enum OffsetMode {
  INCREASE = 'increase',
  DECREASE = 'decrease',
}

interface BidBatchEditProps {
  data: ManualKeywordType[];
  effectiveKeys: Set<ManualKeywordType[typeof MANUAL_UNIQUE_KEY]>;
  suggestBidMap: Map<string, number>;
  onChange: (v: ManualKeywordType[]) => void;
  onCancel: () => void;
}

export const BidBatchEdit: FC<BidBatchEditProps> = ({
  data,
  effectiveKeys,
  suggestBidMap,
  onChange,
  onCancel,
}) => {
  const [priceMode, setPriceMode] = useState(PriceMode.OFFSET_PRICE);
  const [offsetMode, setOffsetMode] = useState(OffsetMode.INCREASE);
  const [offsetNum, setOffsetNum] = useState(0);
  const [absolutePrice, setAbsolutePrice] = useState(0);

  const handlePriceModeChange = (newPriceMode) => {
    setPriceMode(newPriceMode);
  };

  const handleOffsetModeChange = (newOffsetMode) => {
    setOffsetMode(newOffsetMode);
  };

  const handleChange = () => {
    const newData = data.map((item) => {
      if (!effectiveKeys.has(item[MANUAL_UNIQUE_KEY])) return item;

      switch (priceMode) {
        case PriceMode.OFFSET_PRICE: {
          const coefficient = {
            [OffsetMode.INCREASE]: 1,
            [OffsetMode.DECREASE]: -1,
          };
          return {
            ...item,
            [ManualKeywordProperty.PRICE]: Math.max(
              0,
              item[ManualKeywordProperty.PRICE] +
                offsetNum * coefficient[offsetMode],
            ),
          };
        }
        case PriceMode.ABSOLUTE_PRICE: {
          return { ...item, [ManualKeywordProperty.PRICE]: absolutePrice };
        }
        case PriceMode.RECOMMENDED_PRICE: {
          return {
            ...item,
            [ManualKeywordProperty.PRICE]:
              suggestBidMap.get(item[MANUAL_UNIQUE_KEY]) ||
              item[ManualKeywordProperty.PRICE],
          };
        }
        default: {
          return item;
        }
      }
    });

    onChange(newData);
  };

  return (
    <div className={styles.BidBatchEdit}>
      <h2 className='text-[24px]'>批量編輯出價</h2>
      <RadioGroup
        value={priceMode}
        onChange={handlePriceModeChange}
        className='gap-[24px]'
      >
        <RadioCell
          value={PriceMode.OFFSET_PRICE}
          label='提高/降低 點擊出價'
          customRight={
            <div className='flex flex-1 items-center justify-end gap-[12px]'>
              <div className='relative'>
                <RsSelect
                  data={[
                    { value: OffsetMode.INCREASE, label: '提高' },
                    { value: OffsetMode.DECREASE, label: '降低' },
                  ]}
                  value={offsetMode}
                  onChange={handleOffsetModeChange}
                  cleanable={false}
                  size={RsSelectSize.SMALL}
                />
                <div className='absolute flex items-center gap-[6px] pt-[2px]'>
                  <InfoOutlineIcon className='w-[12px] text-[--rs-text-secondary]' />
                  <Hint className='w-max'>
                    原出價為7元，提高2元後，會變成9元
                  </Hint>
                </div>
              </div>
              <BlurNumberInput
                num={offsetNum}
                onChange={setOffsetNum}
                size={RsInputSize.SMALL}
              />
            </div>
          }
        />
        <RadioCell
          value={PriceMode.ABSOLUTE_PRICE}
          label='將點擊出價設定為相同金額'
          customRight={
            <div className='flex flex-1 items-center justify-end gap-[12px]'>
              <BlurNumberInput
                num={absolutePrice}
                onChange={setAbsolutePrice}
                size={RsInputSize.SMALL}
              />
            </div>
          }
        />
        <RadioCell
          value={PriceMode.RECOMMENDED_PRICE}
          label='將點擊出價套用為推薦價格'
          customBottom={
            <Form.HelpText className='-mt-2'>
              手動加入的字因為未有推薦價格，將維持您的設定出價
            </Form.HelpText>
          }
        />
      </RadioGroup>
      <div className='mt-[24px] flex gap-[12px]'>
        <RsButton appearance='ghost' onClick={onCancel}>
          取消
        </RsButton>
        <RsButton appearance='primary' onClick={handleChange}>
          確認
        </RsButton>
      </div>
    </div>
  );
};
